// ============================================================
// Orymia Shared — SpaceDescriptor patch utilities
// Pure merge/extract helpers — no validation (server validates via spaceValidator)
// ============================================================

import type { SpaceDescriptor, SpaceDescriptorPatch } from "./space";

/** Merge a patch into an existing descriptor, preserving identity fields. */
export function mergeSpacePatch(
  existing: SpaceDescriptor,
  patch: SpaceDescriptorPatch,
): SpaceDescriptor {
  return {
    ...existing,
    ...patch,
    id: existing.id,
    slug: existing.slug,
    createdAt: existing.createdAt,
  };
}

/** Pick normalized values from a merged descriptor for keys present in the raw patch. */
export function normalizePatchKeys(
  merged: SpaceDescriptor,
  rawPatch: SpaceDescriptorPatch,
): SpaceDescriptorPatch {
  const normalized: SpaceDescriptorPatch = {};

  for (const key of Object.keys(rawPatch) as (keyof SpaceDescriptorPatch)[]) {
    normalized[key] = merged[key] as never;
  }

  return normalized;
}

/** Extract editable semantic fields from a full descriptor (create-mode fallback). */
export function descriptorToPatch(
  descriptor: SpaceDescriptor,
): SpaceDescriptorPatch {
  return {
    name: descriptor.name,
    ...(descriptor.nameZh !== undefined && { nameZh: descriptor.nameZh }),
    description: descriptor.description,
    mood: descriptor.mood,
    ...(descriptor.hero !== undefined && { hero: descriptor.hero }),
    terrain: descriptor.terrain,
    sky: descriptor.sky,
    ...(descriptor.conditions !== undefined && {
      conditions: descriptor.conditions,
    }),
    ...(descriptor.medium !== undefined && { medium: descriptor.medium }),
    timeOfDay: descriptor.timeOfDay,
    ...(descriptor.objects !== undefined && { objects: descriptor.objects }),
    ...(descriptor.cameraHome !== undefined && {
      cameraHome: descriptor.cameraHome,
    }),
    ...(descriptor.soundtracks !== undefined && {
      soundtracks: descriptor.soundtracks,
    }),
    category: descriptor.category,
  };
}
